import React, { useState } from 'react';

/**
 * Wizard for creating FFmpeg presets step by step.
 * Guides the user through media type, encoding settings, and advanced options.
 *
 * Props:
 *  - onComplete: (presetData) => void - Called with generated preset data
 *  - onCancel: () => void
 *  - initialData: { name, role, command, description, tags, isDefault } - For editing existing presets
 */
export default function FfmpegPresetWizard({ onComplete, onCancel, initialData = null }) {
  const [step, setStep] = useState(1);

  // Wizard state
  const [presetName, setPresetName] = useState(initialData?.name || '');
  const [mediaType, setMediaType] = useState(initialData?.role || 'video'); // video|audio|any
  const [description, setDescription] = useState(initialData?.description || '');
  const [tags, setTags] = useState(initialData?.tags ? (Array.isArray(initialData.tags) ? initialData.tags.join(',') : initialData.tags) : '');
  const [isDefault, setIsDefault] = useState(initialData?.isDefault || false);

  // Video settings
  const [videoCodec, setVideoCodec] = useState('libx264');
  const [resolution, setResolution] = useState('1920x1080');
  const [videoBitrate, setVideoBitrate] = useState('5000k');
  const [framerate, setFramerate] = useState('30');
  const [preset, setPreset] = useState('medium');

  // Audio settings
  const [audioCodec, setAudioCodec] = useState('aac');
  const [audioBitrate, setAudioBitrate] = useState('320k');
  const [sampleRate, setSampleRate] = useState('48000');
  const [audioChannels, setAudioChannels] = useState('2');

  // Output format
  const [outputFormat, setOutputFormat] = useState('dash');
  const [segmentLength, setSegmentLength] = useState('1');
  const [targetLatency, setTargetLatency] = useState('4');
  const [outputPath, setOutputPath] = useState('/var/www/html/cam.mpd');
  const [segmentPattern, setSegmentPattern] = useState('chunk-stream$RepresentationID$-$Number%05d$.m4s');

  // ABR (Adaptive Bitrate) configuration
  const [enableABR, setEnableABR] = useState(false);
  const [abrLadder, setAbrLadder] = useState([
    { resolution: '3840x2160', bitrate: '15000k', label: '4K' },
    { resolution: '2560x1440', bitrate: '8000k', label: '2K' },
    { resolution: '1920x1080', bitrate: '5000k', label: '1080p' },
    { resolution: '1280x720', bitrate: '3000k', label: '720p' }
  ]);

  // Advanced options
  const [gopSize, setGopSize] = useState('30');
  const [hardwareAccel, setHardwareAccel] = useState('none');
  const [customFilters, setCustomFilters] = useState('');
  const [customFlags, setCustomFlags] = useState('');

  // Multi-quality output
  const [multiQuality, setMultiQuality] = useState(false);
  const [includeAdvancedX264, setIncludeAdvancedX264] = useState(true);

  // Advanced x264 parameters
  const [x264Profile, setX264Profile] = useState('high');
  const [x264Level, setX264Level] = useState('5.1');
  const [x264Tune, setX264Tune] = useState('film');
  const [maxrate, setMaxrate] = useState('');
  const [bufsize, setBufsize] = useState('');

  // RTMP input flags
  const [useWallclockTimestamps, setUseWallclockTimestamps] = useState(true);
  const [useGenpts, setUseGenpts] = useState(true);
  const [threadQueueSize, setThreadQueueSize] = useState('9999');
  const [rtbufsize, setRtbufsize] = useState('512M');
  const [useRealtime, setUseRealtime] = useState(false);

  // Calculate total steps based on media type
  // Video: 1-Basic, 2-Video, 3-Output, 4-Advanced, 5-Review = 5 steps
  // Audio: 1-Basic, 2-Audio, 3-Output, 4-Advanced, 5-Review = 5 steps
  // Any: 1-Basic, 2-Video, 3-Audio, 4-Output, 5-Advanced, 6-Review = 6 steps
  const totalSteps = mediaType === 'any' ? 6 : 5;

  const generateCommand = () => {
    let cmd = 'ffmpeg';

    // Realtime flag (for audio mainly)
    if (useRealtime || mediaType === 'audio') {
      cmd += ' -re';
    }

    // Thread queue size and buffer size
    if (threadQueueSize && (mediaType === 'audio' || mediaType === 'any')) {
      cmd += ` -thread_queue_size ${threadQueueSize}`;
    }
    if (rtbufsize && (mediaType === 'audio' || mediaType === 'any')) {
      cmd += ` -rtbufsize ${rtbufsize}`;
    }

    // RTMP input flags
    if (useWallclockTimestamps && mediaType === 'video') {
      cmd += ' -use_wallclock_as_timestamps 1';
    }
    if (useGenpts && mediaType === 'video') {
      cmd += ' -fflags +genpts';
    }

    cmd += ' -i rtmp://localhost:1935/stream/cam';

    // Hardware acceleration (before encoding)
    if (hardwareAccel !== 'none') {
      if (hardwareAccel === 'nvenc') {
        cmd += ' -hwaccel cuda';
      } else if (hardwareAccel === 'qsv') {
        cmd += ' -hwaccel qsv';
      } else if (hardwareAccel === 'vaapi') {
        cmd += ' -hwaccel vaapi';
      }
    }

    // Video encoding
    if (mediaType === 'video' || mediaType === 'any') {
      if (enableABR && abrLadder.length > 1) {
        // ABR (Adaptive Bitrate) with multiple quality levels
        const splitOutputs = abrLadder.map((_, i) => `[v${i}]`).join('');
        cmd += ` -filter_complex "[0:v]split=${abrLadder.length}${splitOutputs}`;

        // Add scale filters for each quality level
        abrLadder.forEach((level, i) => {
          const [w, h] = level.resolution.split('x');
          cmd += `;[v${i}]scale=w=${w}:h=${h}:flags=bicubic,format=yuv420p,setsar=1[v${i}out]`;
        });
        cmd += '"';

        // Map and encode each quality level
        abrLadder.forEach((level, i) => {
          const bitrateNum = parseInt(level.bitrate);
          const maxrateBitrate = level.bitrate;
          const bufsizeBitrate = (bitrateNum * 2) + 'k';

          cmd += ` -map "[v${i}out]"`;
          cmd += ` -r ${framerate}`;
          cmd += ` -c:v:${i} ${videoCodec}`;
          cmd += ` -preset ${preset}`;
          if (x264Tune !== 'none') cmd += ` -tune ${x264Tune}`;
          cmd += ` -profile:v:${i} ${x264Profile}`;
          cmd += ` -level:v:${i} ${x264Level}`;
          cmd += ` -b:v:${i} ${level.bitrate}`;
          cmd += ` -maxrate:v:${i} ${maxrateBitrate}`;
          cmd += ` -bufsize:v:${i} ${bufsizeBitrate}`;
          cmd += ` -g ${gopSize}`;
          cmd += ` -keyint_min ${gopSize}`;
          cmd += ' -sc_threshold 0';
          if (includeAdvancedX264 && videoCodec === 'libx264') {
            const ref = i === 0 ? 4 : 3; // Higher ref for highest quality
            cmd += ` -x264-params:v:${i} "rc-lookahead=40:bframes=3:b-adapt=2:aq-mode=3:aq-strength=1.2:qcomp=0.75:ref=${ref}:nal-hrd=vbr"`;
          }
        });
      } else if (multiQuality) {
        // Legacy multi-quality output (4K + 1080p)
        const res4k = '3840:2160';
        const res1080 = '1920:1080';
        cmd += ` -filter_complex "[0:v]split=2[v4k][v1080];[v4k]scale=w=${res4k.split(':')[0]}:h=${res4k.split(':')[1]}:flags=bicubic,format=yuv420p,setsar=1[v4kout];[v1080]scale=w=${res1080.split(':')[0]}:h=${res1080.split(':')[1]}:flags=bicubic,format=yuv420p,setsar=1[v1080out]"`;

        // 4K output
        cmd += ' -map "[v4kout]"';
        cmd += ` -r ${framerate}`;
        cmd += ` -c:v:0 ${videoCodec}`;
        cmd += ` -preset ${preset}`;
        if (x264Tune !== 'none') cmd += ` -tune ${x264Tune}`;
        cmd += ` -profile:v:0 ${x264Profile}`;
        cmd += ` -level:v:0 ${x264Level}`;
        cmd += ' -b:v:0 15M';
        cmd += ' -maxrate:v:0 15M';
        cmd += ' -bufsize:v:0 30M';
        cmd += ` -g ${gopSize}`;
        cmd += ` -keyint_min ${gopSize}`;
        cmd += ' -sc_threshold 0';
        if (includeAdvancedX264 && videoCodec === 'libx264') {
          cmd += ' -x264-params:v:0 "rc-lookahead=40:bframes=3:b-adapt=2:aq-mode=3:aq-strength=1.2:qcomp=0.75:ref=4:nal-hrd=vbr"';
        }

        // 1080p output
        cmd += ' -map "[v1080out]"';
        cmd += ` -r ${framerate}`;
        cmd += ` -c:v:1 ${videoCodec}`;
        cmd += ` -preset ${preset}`;
        if (x264Tune !== 'none') cmd += ` -tune ${x264Tune}`;
        cmd += ` -profile:v:1 ${x264Profile}`;
        cmd += ` -level:v:1 ${x264Level}`;
        cmd += ' -b:v:1 6M';
        cmd += ' -maxrate:v:1 6M';
        cmd += ' -bufsize:v:1 12M';
        cmd += ` -g ${gopSize}`;
        cmd += ` -keyint_min ${gopSize}`;
        cmd += ' -sc_threshold 0';
        if (includeAdvancedX264 && videoCodec === 'libx264') {
          cmd += ' -x264-params:v:1 "rc-lookahead=40:bframes=3:b-adapt=2:aq-mode=3:aq-strength=1.2:qcomp=0.75:ref=3:nal-hrd=vbr"';
        }
      } else {
        // Single quality output
        // Video codec
        if (hardwareAccel === 'nvenc' && videoCodec === 'libx264') {
          cmd += ' -c:v h264_nvenc';
        } else if (hardwareAccel === 'qsv' && videoCodec === 'libx264') {
          cmd += ' -c:v h264_qsv';
        } else {
          cmd += ` -c:v ${videoCodec}`;
        }

        // Resolution (using scale filter if not source)
        if (resolution !== 'source' && !customFilters.includes('scale')) {
          const [w, h] = resolution.split('x');
          cmd += ` -vf "scale=w=${w}:h=${h}:flags=bicubic,format=yuv420p,setsar=1"`;
        } else if (customFilters.trim()) {
          cmd += ` -vf "${customFilters}"`;
        }

        // Framerate
        if (framerate !== 'source') {
          cmd += ` -r ${framerate}`;
        }

        // Encoding preset
        if (videoCodec === 'libx264' || videoCodec === 'libx265') {
          cmd += ` -preset ${preset}`;
          if (x264Tune !== 'none') cmd += ` -tune ${x264Tune}`;
        }

        // Profile and level
        if (videoCodec === 'libx264' || videoCodec === 'libx265') {
          cmd += ` -profile:v ${x264Profile}`;
          cmd += ` -level:v ${x264Level}`;
        }

        // Video bitrate
        cmd += ` -b:v ${videoBitrate}`;

        // Maxrate and bufsize
        const bitrateMaxrate = maxrate || videoBitrate;
        const bitrateBufsize = bufsize || (parseInt(videoBitrate) * 2 + 'k');
        cmd += ` -maxrate ${bitrateMaxrate}`;
        cmd += ` -bufsize ${bitrateBufsize}`;

        // GOP size
        cmd += ` -g ${gopSize}`;
        cmd += ` -keyint_min ${gopSize}`;
        cmd += ' -sc_threshold 0';

        // Advanced x264 parameters
        if (includeAdvancedX264 && videoCodec === 'libx264') {
          cmd += ' -x264-params "rc-lookahead=40:bframes=3:b-adapt=2:aq-mode=3:aq-strength=1.2:qcomp=0.75:ref=4:nal-hrd=vbr"';
        }
      }
    } else {
      // Audio-only mode
      cmd += ' -vn';
    }

    // Audio encoding
    if (mediaType === 'audio' || mediaType === 'any') {
      // Map audio stream explicitly for audio-only
      if (mediaType === 'audio') {
        cmd += ' -map 0:a:0';
      }

      cmd += ` -c:a ${audioCodec}`;
      cmd += ` -b:a ${audioBitrate}`;
      cmd += ` -ac ${audioChannels}`;
      cmd += ` -ar ${sampleRate}`;

      // GOP settings for audio (for proper sync)
      if (mediaType === 'audio') {
        cmd += ` -g ${gopSize}`;
        cmd += ` -keyint_min ${gopSize}`;
        cmd += ' -forced_idr 1';
        cmd += ' -sc_threshold 0';
        cmd += ' -b_strategy 0';
      }
    } else {
      // Video-only mode
      cmd += ' -an';
    }

    // Output format specific settings
    if (outputFormat === 'hls') {
      cmd += ' -f hls';
      cmd += ` -hls_time ${segmentLength}`;
      cmd += ' -hls_playlist_type vod';
      cmd += ' -hls_segment_filename "segment_%03d.ts"';
      cmd += ' output.m3u8';
    } else if (outputFormat === 'dash') {
      // Audio-specific DASH configuration
      if (mediaType === 'audio') {
        cmd += ' -utc_timing_url https://time.akamai.com/?iso';
        cmd += ' -format_options movflags=cmaf';
        cmd += ' -frag_type duration';
        cmd += ' -dash_segment_type mp4';
        cmd += ' -use_timeline 0';
        cmd += ' -use_template 1';
        cmd += ' -streaming 1';
        cmd += ' -remove_at_exit 1';
        cmd += ' -index_correction 1';
        cmd += ' -ignore_io_errors 1';
        cmd += ' -ldash 1';
        cmd += ` -target_latency ${targetLatency}`;
        cmd += ' -export_side_data 1';
        cmd += ' -start_number 1';
        cmd += ' -window_size 600';
        cmd += ` -adaptation_sets id=0,seg_duration=${segmentLength},frag_duration=${segmentLength},streams=a`;
        cmd += ' -hls_playlist true';
        cmd += ' -f dash';
        cmd += ' /var/www/html/cam.mpd';
      } else {
        // Video/mixed DASH configuration
        cmd += ' -f dash';
        cmd += ` -seg_duration ${segmentLength}`;
        cmd += ` -min_seg_duration ${parseInt(segmentLength) * 1000000}`;
        cmd += ' -use_template 1';
        cmd += ' -use_timeline 1';
        cmd += ' -streaming 1';
        cmd += ` -target_latency ${targetLatency}`;
        cmd += ' -dash_segment_type mp4';
        cmd += ' -utc_timing_url https://time.akamai.com/?iso';
        cmd += ' -hls_playlist 1';
        cmd += ' -remove_at_exit 0';
        if (segmentPattern.trim()) {
          cmd += ` -init_seg_name 'init-stream$RepresentationID$.m4s'`;
          cmd += ` -media_seg_name '${segmentPattern}'`;
        }
        cmd += ` ${outputPath || 'output.mpd'}`;
      }
    } else if (outputFormat === 'dash+hls') {
      // Dual output: DASH + HLS
      cmd += ' -f dash';
      cmd += ` -seg_duration ${segmentLength}`;
      cmd += ` -min_seg_duration ${parseInt(segmentLength) * 1000000}`;
      cmd += ' -use_template 1';
      cmd += ' -use_timeline 1';
      cmd += ' -streaming 1';
      cmd += ` -target_latency ${targetLatency}`;
      cmd += ' -dash_segment_type mp4';
      cmd += ' -utc_timing_url https://time.akamai.com/?iso';
      cmd += ' -hls_playlist 1';
      cmd += ' -remove_at_exit 0';
      if (segmentPattern.trim()) {
        cmd += ` -init_seg_name 'init-stream$RepresentationID$.m4s'`;
        cmd += ` -media_seg_name '${segmentPattern}'`;
      }
      cmd += ` ${outputPath || 'output.mpd'}`;
    } else if (outputFormat === 'mp4') {
      cmd += ' -f mp4';
      cmd += ' -movflags +faststart';
      cmd += ' output.mp4';
    } else if (outputFormat === 'mkv') {
      cmd += ' -f matroska';
      cmd += ' output.mkv';
    } else if (outputFormat === 'webm') {
      cmd += ' -f webm';
      cmd += ' output.webm';
    }

    // Custom flags
    if (customFlags.trim()) {
      cmd += ` ${customFlags}`;
    }

    return cmd;
  };

  const handleComplete = () => {
    const command = generateCommand();
    const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);

    onComplete({
      name: presetName.trim(),
      role: mediaType,
      command: command,
      description: description,
      tags: tagsArray,
      isDefault: isDefault
    });
  };

  const btn = (bg) => ({ background: bg, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', boxShadow: '0 2px 8px rgba(154,81,226,0.15)', transition: 'all 0.2s' });
  const input = { width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #555', background: '#181828', color: '#fff', boxSizing: 'border-box' };
  const label = { fontSize: 13, fontWeight: 500, color: '#9a51e2', marginBottom: 6, display: 'block' };
  const radio = { display: 'flex', gap: 16, flexWrap: 'wrap' };
  const radioOption = { display: 'flex', alignItems: 'center', gap: 6, color: '#fff' };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div style={{ display: 'grid', gap: 16 }}>
            <h3 style={{ margin: 0 }}>Step 1: Basic Information</h3>

            <div>
              <label style={label}>Preset Name *</label>
              <input
                style={input}
                value={presetName}
                onChange={e => setPresetName(e.target.value)}
                placeholder="e.g., High Quality 1080p"
              />
            </div>

            <div>
              <label style={label}>Media Type *</label>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setMediaType('video')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: '2px solid',
                    borderColor: mediaType === 'video' ? '#9a51e2' : '#555',
                    background: mediaType === 'video' ? 'linear-gradient(135deg, #9a51e2 0%, #7a3fb7 100%)' : '#23243a',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: mediaType === 'video' ? 600 : 400,
                    fontSize: 14,
                    transition: 'all 0.2s',
                    boxShadow: mediaType === 'video' ? '0 4px 12px rgba(154,81,226,0.3)' : 'none',
                  }}
                >
                  🎥 Video Only
                </button>
                <button
                  type="button"
                  onClick={() => setMediaType('audio')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: '2px solid',
                    borderColor: mediaType === 'audio' ? '#9a51e2' : '#555',
                    background: mediaType === 'audio' ? 'linear-gradient(135deg, #9a51e2 0%, #7a3fb7 100%)' : '#23243a',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: mediaType === 'audio' ? 600 : 400,
                    fontSize: 14,
                    transition: 'all 0.2s',
                    boxShadow: mediaType === 'audio' ? '0 4px 12px rgba(154,81,226,0.3)' : 'none',
                  }}
                >
                  🎵 Audio Only
                </button>
                <button
                  type="button"
                  onClick={() => setMediaType('any')}
                  style={{
                    padding: '10px 20px',
                    borderRadius: 8,
                    border: '2px solid',
                    borderColor: mediaType === 'any' ? '#9a51e2' : '#555',
                    background: mediaType === 'any' ? 'linear-gradient(135deg, #9a51e2 0%, #7a3fb7 100%)' : '#23243a',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: mediaType === 'any' ? 600 : 400,
                    fontSize: 14,
                    transition: 'all 0.2s',
                    boxShadow: mediaType === 'any' ? '0 4px 12px rgba(154,81,226,0.3)' : 'none',
                  }}
                >
                  🎬 Video + Audio
                </button>
              </div>
            </div>

            <div>
              <label style={label}>Description</label>
              <input
                style={input}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief description of this preset"
              />
            </div>

            <div>
              <label style={label}>Tags (comma separated)</label>
              <input
                style={input}
                value={tags}
                onChange={e => setTags(e.target.value)}
                placeholder="e.g., streaming, high-quality, production"
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
              Set as default preset for this media type
            </label>
          </div>
        );

      case 2:
        if (mediaType === 'audio') {
          // Skip to audio settings
          return renderAudioSettings();
        }
        return (
          <div style={{ display: 'grid', gap: 16 }}>
            <h3 style={{ margin: 0 }}>Step 2: Video Encoding</h3>

            <div>
              <label style={label}>Video Codec</label>
              <select style={input} value={videoCodec} onChange={e => setVideoCodec(e.target.value)}>
                <option value="libx264">H.264 (libx264)</option>
                <option value="libx265">H.265/HEVC (libx265)</option>
                <option value="libvpx-vp9">VP9 (libvpx-vp9)</option>
                <option value="libaom-av1">AV1 (libaom-av1)</option>
                <option value="copy">Copy (no re-encoding)</option>
              </select>
            </div>

            <div>
              <label style={label}>Resolution</label>
              <select style={input} value={resolution} onChange={e => setResolution(e.target.value)}>
                <option value="source">Keep source resolution</option>
                <option value="3840x2160">4K (3840x2160)</option>
                <option value="2560x1440">2K (2560x1440)</option>
                <option value="1920x1080">Full HD (1920x1080)</option>
                <option value="1280x720">HD (1280x720)</option>
                <option value="854x480">SD (854x480)</option>
                <option value="640x360">360p</option>
              </select>
            </div>

            <div>
              <label style={label}>Video Bitrate</label>
              <select style={input} value={videoBitrate} onChange={e => setVideoBitrate(e.target.value)}>
                <option value="1000k">1 Mbps</option>
                <option value="2500k">2.5 Mbps</option>
                <option value="5000k">5 Mbps</option>
                <option value="8000k">8 Mbps</option>
                <option value="10000k">10 Mbps</option>
                <option value="15000k">15 Mbps</option>
                <option value="20000k">20 Mbps</option>
                <option value="30000k">30 Mbps</option>
                <option value="50000k">50 Mbps</option>
              </select>
            </div>

            <div>
              <label style={label}>Frame Rate</label>
              <select style={input} value={framerate} onChange={e => setFramerate(e.target.value)}>
                <option value="source">Keep source framerate</option>
                <option value="23.976">23.976 fps (Film)</option>
                <option value="24">24 fps</option>
                <option value="25">25 fps (PAL)</option>
                <option value="29.97">29.97 fps (NTSC)</option>
                <option value="30">30 fps</option>
                <option value="50">50 fps</option>
                <option value="60">60 fps</option>
              </select>
            </div>

            {(videoCodec === 'libx264' || videoCodec === 'libx265') && (
              <div>
                <label style={label}>Encoding Preset (speed vs compression)</label>
                <select style={input} value={preset} onChange={e => setPreset(e.target.value)}>
                  <option value="ultrafast">Ultrafast (fastest, largest file)</option>
                  <option value="superfast">Superfast</option>
                  <option value="veryfast">Very Fast</option>
                  <option value="faster">Faster</option>
                  <option value="fast">Fast</option>
                  <option value="medium">Medium (balanced)</option>
                  <option value="slow">Slow</option>
                  <option value="slower">Slower</option>
                  <option value="veryslow">Very Slow (slowest, smallest file)</option>
                </select>
              </div>
            )}
          </div>
        );

      case 3:
        if (mediaType === 'video') {
          // Video: step 3 is output format
          return renderOutputFormat();
        }
        // Audio or any: step 3 is audio settings
        return renderAudioSettings();

      case 4:
        if (mediaType === 'video') {
          // Video: step 4 is advanced options
          return renderAdvancedOptions();
        }
        // Audio or any: step 4 is output format
        return renderOutputFormat();

      case 5:
        if (mediaType === 'video' || mediaType === 'audio') {
          // Video/Audio: step 5 is review
          return renderReview();
        }
        // Any: step 5 is advanced options
        return renderAdvancedOptions();

      case 6:
        // Any: step 6 is review
        return renderReview();

      default:
        return null;
    }
  };

  const renderAudioSettings = () => (
    <div style={{ display: 'grid', gap: 16 }}>
      <h3 style={{ margin: 0 }}>Step {mediaType === 'audio' ? '2' : '3'}: Audio Encoding</h3>

      <div>
        <label style={label}>Audio Codec</label>
        <select style={input} value={audioCodec} onChange={e => setAudioCodec(e.target.value)}>
          <option value="aac">AAC</option>
          <option value="libmp3lame">MP3</option>
          <option value="libopus">Opus</option>
          <option value="libvorbis">Vorbis</option>
          <option value="flac">FLAC</option>
          <option value="copy">Copy (no re-encoding)</option>
        </select>
      </div>

      <div>
        <label style={label}>Audio Bitrate</label>
        <select style={input} value={audioBitrate} onChange={e => setAudioBitrate(e.target.value)}>
          <option value="64k">64 kbps</option>
          <option value="96k">96 kbps</option>
          <option value="128k">128 kbps</option>
          <option value="160k">160 kbps</option>
          <option value="192k">192 kbps</option>
          <option value="256k">256 kbps</option>
          <option value="320k">320 kbps</option>
        </select>
      </div>

      <div>
        <label style={label}>Sample Rate</label>
        <select style={input} value={sampleRate} onChange={e => setSampleRate(e.target.value)}>
          <option value="44100">44.1 kHz (CD quality)</option>
          <option value="48000">48 kHz (standard)</option>
          <option value="96000">96 kHz (high quality)</option>
          <option value="192000">192 kHz (studio quality)</option>
        </select>
      </div>

      <div>
        <label style={label}>Audio Channels</label>
        <select style={input} value={audioChannels} onChange={e => setAudioChannels(e.target.value)}>
          <option value="1">Mono (1 channel)</option>
          <option value="2">Stereo (2 channels)</option>
          <option value="6">5.1 Surround (6 channels)</option>
          <option value="8">7.1 Surround (8 channels)</option>
        </select>
      </div>
    </div>
  );

  const renderOutputFormat = () => {
    const stepNum = mediaType === 'video' ? 3 : mediaType === 'audio' ? 3 : 4;
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <h3 style={{ margin: 0 }}>Step {stepNum}: Output Format</h3>

        <div>
          <label style={label}>Output Format</label>
          <select style={input} value={outputFormat} onChange={e => setOutputFormat(e.target.value)}>
            <option value="dash">DASH (Dynamic Adaptive Streaming)</option>
            <option value="dash+hls">DASH + HLS (Dual output)</option>
            <option value="hls">HLS (HTTP Live Streaming)</option>
            <option value="mp4">MP4 (MPEG-4)</option>
            <option value="mkv">MKV (Matroska)</option>
            <option value="webm">WebM</option>
          </select>
        </div>

        {(outputFormat === 'hls' || outputFormat === 'dash' || outputFormat === 'dash+hls') && (
          <>
            <div>
              <label style={label}>Segment Length (seconds)</label>
              <select style={input} value={segmentLength} onChange={e => setSegmentLength(e.target.value)}>
                <option value="1">1 second (low latency)</option>
                <option value="2">2 seconds</option>
                <option value="4">4 seconds</option>
                <option value="6">6 seconds</option>
                <option value="10">10 seconds</option>
              </select>
            </div>

            <div>
              <label style={label}>Target Latency (seconds)</label>
              <select style={input} value={targetLatency} onChange={e => setTargetLatency(e.target.value)}>
                <option value="2">2 seconds (ultra low latency)</option>
                <option value="4">4 seconds (low latency)</option>
                <option value="6">6 seconds (balanced)</option>
                <option value="8">8 seconds</option>
                <option value="10">10 seconds</option>
              </select>
              <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
                Controls the target latency for DASH streaming. Lower values reduce latency but may impact stability.
              </div>
            </div>

            <div>
              <label style={label}>Output File Path</label>
              <input
                style={input}
                value={outputPath}
                onChange={e => setOutputPath(e.target.value)}
                placeholder="/var/www/html/cam.mpd"
              />
              <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
                Full path where the manifest file will be saved.
              </div>
            </div>

            <div>
              <label style={label}>Segment Naming Pattern</label>
              <input
                style={input}
                value={segmentPattern}
                onChange={e => setSegmentPattern(e.target.value)}
                placeholder="chunk-stream$RepresentationID$-$Number%05d$.m4s"
              />
              <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
                Pattern for segment file names. Use $RepresentationID$ and $Number%05d$ for templating.
              </div>
            </div>
          </>
        )}

        <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, fontSize: 13, color: '#e0e0e0' }}>
          <strong>Format Info:</strong>
          {outputFormat === 'hls' && ' HLS is widely supported for streaming, especially on Apple devices.'}
          {outputFormat === 'dash' && ' DASH is an adaptive streaming format with broad support across platforms. Includes low-latency streaming options.'}
          {outputFormat === 'dash+hls' && ' Dual output generates both DASH and HLS playlists from the same segments for maximum compatibility.'}
          {outputFormat === 'mp4' && ' MP4 is universally compatible for progressive download and playback.'}
          {outputFormat === 'mkv' && ' MKV supports multiple audio/subtitle tracks and advanced features.'}
          {outputFormat === 'webm' && ' WebM is optimized for web playback with VP9/AV1 and Opus codecs.'}
        </div>
      </div>
    );
  };

  const renderAdvancedOptions = () => {
    const stepNum = mediaType === 'video' ? 4 : mediaType === 'audio' ? 4 : 5;
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <h3 style={{ margin: 0 }}>Step {stepNum}: Advanced Options</h3>

        {(mediaType === 'video' || mediaType === 'any') && (
          <>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={enableABR}
                onChange={e => {
                  setEnableABR(e.target.checked);
                  if (e.target.checked) setMultiQuality(false);
                }}
              />
              <span style={{ fontWeight: 500, color: '#fff' }}>Enable ABR (Adaptive Bitrate Ladder)</span>
            </label>

            {enableABR && (
              <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8, color: '#9a51e2' }}>ABR Quality Levels</div>
                {abrLadder.map((level, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input
                      style={{ ...input, flex: 1 }}
                      value={level.label}
                      onChange={e => {
                        const newLadder = [...abrLadder];
                        newLadder[i].label = e.target.value;
                        setAbrLadder(newLadder);
                      }}
                      placeholder="Label (e.g., 1080p)"
                    />
                    <input
                      style={{ ...input, flex: 1 }}
                      value={level.resolution}
                      onChange={e => {
                        const newLadder = [...abrLadder];
                        newLadder[i].resolution = e.target.value;
                        setAbrLadder(newLadder);
                      }}
                      placeholder="Resolution (e.g., 1920x1080)"
                    />
                    <input
                      style={{ ...input, flex: 1 }}
                      value={level.bitrate}
                      onChange={e => {
                        const newLadder = [...abrLadder];
                        newLadder[i].bitrate = e.target.value;
                        setAbrLadder(newLadder);
                      }}
                      placeholder="Bitrate (e.g., 5000k)"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (abrLadder.length > 1) {
                          setAbrLadder(abrLadder.filter((_, idx) => idx !== i));
                        }
                      }}
                      style={{
                        background: '#c62828',
                        color: '#fff',
                        border: 'none',
                        borderRadius: 6,
                        padding: '8px 12px',
                        cursor: 'pointer',
                        fontSize: 14
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    setAbrLadder([...abrLadder, { resolution: '640x360', bitrate: '800k', label: '360p' }]);
                  }}
                  style={{
                    background: 'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: 14,
                    marginTop: 4
                  }}
                >
                  + Add Quality Level
                </button>
              </div>
            )}

            {!enableABR && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={multiQuality}
                  onChange={e => setMultiQuality(e.target.checked)}
                />
                <span style={{ fontWeight: 500 }}>Multi-quality output (4K + 1080p)</span>
              </label>
            )}

            <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, fontSize: 13, color: '#e0e0e0' }}>
              <strong>RTMP Input Options:</strong>
              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
                  <input
                    type="checkbox"
                    checked={useWallclockTimestamps}
                    onChange={e => setUseWallclockTimestamps(e.target.checked)}
                  />
                  Use wallclock timestamps (-use_wallclock_as_timestamps 1)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#fff' }}>
                  <input
                    type="checkbox"
                    checked={useGenpts}
                    onChange={e => setUseGenpts(e.target.checked)}
                  />
                  Generate PTS (-fflags +genpts)
                </label>
              </div>
            </div>

            <div>
              <label style={label}>GOP Size (Group of Pictures)</label>
              <input
                type="number"
                style={input}
                value={gopSize}
                onChange={e => setGopSize(e.target.value)}
                placeholder="30"
              />
              <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
                Typically equals framerate. Controls keyframe interval (30 for 30fps).
              </div>
            </div>

            <div>
              <label style={label}>x264 Profile</label>
              <select style={input} value={x264Profile} onChange={e => setX264Profile(e.target.value)}>
                <option value="baseline">Baseline</option>
                <option value="main">Main</option>
                <option value="high">High (recommended)</option>
                <option value="high10">High 10</option>
                <option value="high422">High 4:2:2</option>
                <option value="high444">High 4:4:4</option>
              </select>
            </div>

            <div>
              <label style={label}>x264 Level</label>
              <select style={input} value={x264Level} onChange={e => setX264Level(e.target.value)}>
                <option value="3.0">3.0</option>
                <option value="3.1">3.1</option>
                <option value="3.2">3.2</option>
                <option value="4.0">4.0</option>
                <option value="4.1">4.1</option>
                <option value="4.2">4.2</option>
                <option value="5.0">5.0</option>
                <option value="5.1">5.1 (4K ready)</option>
                <option value="5.2">5.2</option>
              </select>
            </div>

            <div>
              <label style={label}>x264 Tune</label>
              <select style={input} value={x264Tune} onChange={e => setX264Tune(e.target.value)}>
                <option value="none">None</option>
                <option value="film">Film (recommended for movies)</option>
                <option value="animation">Animation</option>
                <option value="grain">Grain (preserve film grain)</option>
                <option value="stillimage">Still Image</option>
                <option value="fastdecode">Fast Decode</option>
                <option value="zerolatency">Zero Latency</option>
              </select>
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="checkbox"
                checked={includeAdvancedX264}
                onChange={e => setIncludeAdvancedX264(e.target.checked)}
              />
              <span style={{ fontWeight: 500 }}>Include advanced x264 parameters</span>
            </label>
            {includeAdvancedX264 && (
              <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, fontSize: 12, color: '#e0e0e0' }}>
                Will include: rc-lookahead=40:bframes=3:b-adapt=2:aq-mode=3:aq-strength=1.2:qcomp=0.75:ref=4:nal-hrd=vbr
              </div>
            )}

            <div>
              <label style={label}>Hardware Acceleration</label>
              <select style={input} value={hardwareAccel} onChange={e => setHardwareAccel(e.target.value)}>
                <option value="none">None (CPU encoding)</option>
                <option value="nvenc">NVIDIA NVENC (GPU)</option>
                <option value="qsv">Intel Quick Sync (GPU)</option>
                <option value="vaapi">VAAPI (Linux GPU)</option>
              </select>
            </div>

            <div>
              <label style={label}>Video Filters (advanced)</label>
              <input
                style={input}
                value={customFilters}
                onChange={e => setCustomFilters(e.target.value)}
                placeholder="e.g., scale=1280:720,yadif"
              />
              <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
                Optional: scale, crop, deinterlace, etc. (ignored if multi-quality is enabled)
              </div>
            </div>
          </>
        )}

        <div>
          <label style={label}>Custom FFmpeg Flags</label>
          <input
            style={input}
            value={customFlags}
            onChange={e => setCustomFlags(e.target.value)}
            placeholder="e.g., -threads 4"
          />
          <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 4 }}>
            Any additional FFmpeg parameters
          </div>
        </div>

        <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, fontSize: 13, color: '#e0e0e0' }}>
          <strong>Note:</strong> Advanced x264 parameters are enabled by default for optimal quality.
        </div>
      </div>
    );
  };

  const renderReview = () => {
    const command = generateCommand();
    const stepNum = mediaType === 'video' ? 5 : mediaType === 'audio' ? 5 : 6;

    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <h3 style={{ margin: 0 }}>Step {stepNum}: Review & Save</h3>

        <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Preset Summary</div>
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <div><strong>Name:</strong> {presetName || '(not set)'}</div>
            <div><strong>Media Type:</strong> {mediaType}</div>
            {description && <div><strong>Description:</strong> {description}</div>}
            {tags && <div><strong>Tags:</strong> {tags}</div>}
            <div><strong>Default:</strong> {isDefault ? 'Yes' : 'No'}</div>
          </div>
        </div>

        {(mediaType === 'video' || mediaType === 'any') && (
          <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, color: '#fff' }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Video Settings</div>
            <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
              <div><strong>Codec:</strong> {videoCodec}</div>
              <div><strong>Resolution:</strong> {resolution}</div>
              <div><strong>Bitrate:</strong> {videoBitrate}</div>
              <div><strong>Framerate:</strong> {framerate}</div>
              {(videoCodec === 'libx264' || videoCodec === 'libx265') && <div><strong>Preset:</strong> {preset}</div>}
              <div><strong>GOP Size:</strong> {gopSize}</div>
              {hardwareAccel !== 'none' && <div><strong>Hardware Accel:</strong> {hardwareAccel}</div>}
            </div>
          </div>
        )}

        {(mediaType === 'audio' || mediaType === 'any') && (
          <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, color: '#fff' }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Audio Settings</div>
            <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
              <div><strong>Codec:</strong> {audioCodec}</div>
              <div><strong>Bitrate:</strong> {audioBitrate}</div>
              <div><strong>Sample Rate:</strong> {sampleRate} Hz</div>
              <div><strong>Channels:</strong> {audioChannels}</div>
            </div>
          </div>
        )}

        <div style={{ padding: 12, background: '#2d2e4a', borderRadius: 6, color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Output Settings</div>
          <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
            <div><strong>Format:</strong> {outputFormat.toUpperCase()}</div>
            {(outputFormat === 'hls' || outputFormat === 'dash') && <div><strong>Segment Length:</strong> {segmentLength}s</div>}
          </div>
        </div>

        <div>
          <label style={label}>Generated FFmpeg Command</label>
          <pre style={{
            background: '#1e1e1e',
            color: '#d4d4d4',
            padding: 12,
            borderRadius: 6,
            overflowX: 'auto',
            fontSize: 12,
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}>
            {command}
          </pre>
          <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 8 }}>
            You can manually edit this command after saving the preset if needed.
          </div>
        </div>

        {!presetName.trim() && (
          <div style={{ padding: 12, background: '#3d2e2e', borderRadius: 6, color: '#ff6b6b', fontSize: 13 }}>
            Please provide a preset name before saving.
          </div>
        )}
      </div>
    );
  };

  const canProceed = () => {
    if (step === 1) {
      return presetName.trim().length > 0;
    }
    return true;
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleComplete();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1001
    }}>
      <div style={{
        width: 'min(700px, 95vw)',
        maxHeight: '90vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        background: '#23243a',
        color: '#fff',
        borderRadius: 16,
        padding: 24,
        boxShadow: '0 4px 32px #000'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: '#fff' }}>FFmpeg Preset Wizard</h2>
          <span style={{ marginLeft: 'auto', fontSize: 14, color: '#9a51e2', fontWeight: 500 }}>
            Step {step} of {totalSteps}
          </span>
        </div>

        {/* Progress bar */}
        <div style={{ height: 4, background: '#181828', borderRadius: 2, marginBottom: 24 }}>
          <div style={{
            height: '100%',
            background: 'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)',
            borderRadius: 2,
            width: `${(step / totalSteps) * 100}%`,
            transition: 'width 0.3s ease'
          }} />
        </div>

        {/* Step content */}
        <div style={{ minHeight: 300 }}>
          {renderStep()}
        </div>

        {/* Navigation buttons */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'space-between' }}>
          <button onClick={onCancel} style={btn('#9e9e9e')}>
            Cancel
          </button>
          <div style={{ display: 'flex', gap: 12 }}>
            {step > 1 && (
              <button onClick={handleBack} style={btn('#607d8b')}>
                Back
              </button>
            )}
            <button
              onClick={handleNext}
              style={{...btn(canProceed() ? '#9a51e2' : '#bdbdbd'), background: canProceed() ? 'linear-gradient(90deg, #9a51e2 60%, #7a3fb7 100%)' : '#bdbdbd'}}
              disabled={!canProceed()}
            >
              {step === totalSteps ? 'Save Preset' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
